import {ImageProcessingNodes as Nodes} from "@cm/image-processing-nodes"
import {TextureType} from "@cm/material-nodes"
import type {InputMapsInfo, EncodedConvertedNode} from "#material-maps-exporter/core/types"
import {
    imageProcessingGraphForInputMaps,
    encodeConvertedNode,
    getMapNodeOrProvideFallback,
    convertNormal,
    alphaFromMaskOrTransmission,
} from "#material-maps-exporter/core/image-processing-graph"
import type {PconExportConfig, PCON_EXPORT_DEFAULT_TEXTURE_TYPES} from "#material-maps-exporter/targets/pcon/types"
import {PCON_EXPORT_OPACITY} from "#material-maps-exporter/targets/pcon/types"

export function getMapDimensionInPxForPhysicalDimensionInCm(dimensionCm: number): 1024 | 2048 {
    return dimensionCm < 100 ? 1024 : 2048
}

export function imageProcessingGraphForPconExport(
    config: PconExportConfig,
    mapsInfo: InputMapsInfo,
    exportOpacity: boolean = false,
): Nodes.Struct<Record<string, EncodedConvertedNode>> {
    const mapsNodes = imageProcessingGraphForInputMaps(mapsInfo)

    const targetMapWidthPx = getMapDimensionInPxForPhysicalDimensionInCm(config.commonWidthCm)
    const targetMapHeightPx = getMapDimensionInPxForPhysicalDimensionInCm(config.commonHeightCm)

    for (const type of Object.keys(mapsNodes)) {
        mapsNodes[type] = Nodes.resize(mapsNodes[type], targetMapWidthPx, targetMapHeightPx)
    }

    const defaultTextures: Record<(typeof PCON_EXPORT_DEFAULT_TEXTURE_TYPES)[number], EncodedConvertedNode> = {
        Diffuse: encodeConvertedNode(
            getMapNodeOrProvideFallback(mapsNodes, targetMapWidthPx, targetMapHeightPx, TextureType.Diffuse, [0.0, 0.0, 0.0], "sRGB"),
            config.format,
            undefined,
            true,
            false,
        ),
        Normal: encodeConvertedNode(
            convertNormal(
                getMapNodeOrProvideFallback(mapsNodes, targetMapWidthPx, targetMapHeightPx, TextureType.Normal, [0.5, 0.5, 1.0], "linear"),
                "y+up",
                "y+up",
            ),
            config.format,
            undefined,
            false,
            false,
        ),
        Metalness: encodeConvertedNode(
            getMapNodeOrProvideFallback(mapsNodes, targetMapWidthPx, targetMapHeightPx, TextureType.Metalness, 0.0, "linear"),
            config.format,
            undefined,
            false,
            true,
        ),
        Roughness: encodeConvertedNode(
            getMapNodeOrProvideFallback(mapsNodes, targetMapWidthPx, targetMapHeightPx, TextureType.Roughness, 1.0, "linear"),
            config.format,
            undefined,
            false,
            true,
        ),
    }

    const optionalTextures: Record<string, EncodedConvertedNode> = {}
    if (exportOpacity) {
        const alpha = alphaFromMaskOrTransmission(mapsNodes, mapsInfo)
        if (!alpha) throw new Error("Alpha export was requested but no mask or transmission map was found in the input maps info.")
        optionalTextures[`${PCON_EXPORT_OPACITY}`] = encodeConvertedNode(alpha, config.format, undefined, false, true)
    }

    return Nodes.struct({
        ...defaultTextures,
        ...optionalTextures,
    })
}
